import { Prisma } from "@prisma/client";
import { BuyableItemEvent, BuyableItemHandler } from "../buyable-item.types";

type PartialExportProgramModuleBuyableItem = {
  exportProgramModuleEvent?: { id: string } | null;
};

type ExportProgramModuleBuyableItem = {
  exportProgramModuleEvent: { id: string };
};

type ExportProgramModuleWithEvent = {
  exportProgramModuleEvent: BuyableItemEvent;
};

const GROUP_KEY_PREFIX = "exportProgramModule";

export const ExportProgramModuleBuyableItemHandler = {
  displayName: "Export du module programme",
  accounting: {
    generalAccountCode: "706509",
    invoiceLabel: "Export du module programme",
  },

  getEvent(item: ExportProgramModuleWithEvent): BuyableItemEvent {
    return item.exportProgramModuleEvent;
  },

  isValid(item: PartialExportProgramModuleBuyableItem): boolean {
    return !!item.exportProgramModuleEvent?.id;
  },

  findEquivalent(item: PartialExportProgramModuleBuyableItem): Prisma.BuyableItemWhereInput {
    return {
      exportProgramModuleEvent: {
        id: item.exportProgramModuleEvent?.id,
      },
    };
  },

  create({
    exportProgramModuleEvent,
  }: ExportProgramModuleBuyableItem): Omit<Prisma.BuyableItemCreateInput, "buyableType"> {
    return {
      exportProgramModuleEvent: {
        connect: {
          id: exportProgramModuleEvent.id,
        },
      },
    };
  },

  parseGroupKeyAsWhere(groupKey: string): Prisma.BuyableItemWhereInput {
    if (!ExportProgramModuleBuyableItemHandler.isGroupKey(groupKey)) {
      throw new Error(`Invalid group key for export program module: ${groupKey}`);
    }

    const [, eventId] = groupKey.split("|");
    return {
      exportProgramModuleEvent: {
        id: eventId,
      },
    };
  },

  isGroupKey(groupKey: string): boolean {
    return groupKey.startsWith(`${GROUP_KEY_PREFIX}|`);
  },

  getGroupKey(item: PartialExportProgramModuleBuyableItem): string {
    const eventId = item.exportProgramModuleEvent?.id;
    if (!eventId) {
      throw new Error("Cannot get group key for invalid exportProgramModule item");
    }
    return `${GROUP_KEY_PREFIX}|${eventId}`;
  },

  getPricingQuery(): Prisma.PricingFindFirstArgs {
    return {
      where: {
        backofficePrestation: {
          type: "exportProgramModule",
        },
      },
    };
  },

  getEventSearchFilter(searchText: string): Prisma.BuyableItemWhereInput {
    return {
      exportProgramModuleEvent: {
        name: { contains: searchText, mode: "insensitive" },
      },
    };
  },
} as const satisfies BuyableItemHandler;
